<template>
  <div
    class="d-flex justify-space-between"
    :class="{
      'padding-t-8': !removePadding,
      'padding-t-12 padding-x-32': !isMobile && !removePadding && !paymentUpdate,
      'flex-column': isMobile
    }"
  >
    <div class="max-w-352 min-w-352">
      <template v-if="currentTrip">
        <h2 class="margin-b-3 font-medium">
          Current trip
        </h2>
        <TripReviewMetadata
          :vehicles="currentTrip.vehicles"
          :passengerCount="currentTrip.passengerCount"
          :tripTypeId="currentTrip.tripTypeId"
        />
        <v-card class="background-white margin-t-4">
          <v-card-text class="padding-t-4 padding-b-0 padding-x-4">
            <template
              v-if="
                currentTrip.ada ||
                currentTrip.spab ||
                currentTrip.vehicleNeededEntireTrip
              "
            >
              <TripReviewRequirements
                :ada="currentTrip.ada"
                :spab="currentTrip.spab"
                :vehicleNeededEntireTrip="currentTrip.vehicleNeededEntireTrip"
              />
              <v-divider />
            </template>
            <TripReviewItinerary
              :stops="currentTrip.stops"
              class="margin-t-4"
            />
          </v-card-text>
        </v-card>
      </template>
    </div>
    <div
      class="d-flex align-center"
      :class="{ 'justify-center margin-y-8': isMobile }"
    >
      <CUIcon v-if="isMobile" view-box="0 0 24 24" class="w-24 h-24">
        arrow_downward
      </CUIcon>
      <CUIcon v-else view-box="0 0 24 24" class="w-36 h-36">
        arrow_right
      </CUIcon>
    </div>
    <div class="max-w-352 min-w-352">
      <h2
        class="margin-b-3 font-medium"
        :class="{ 'text-primary': !isDraftCustomerReview }"
      >
        Trip modifications
      </h2>
      <template v-if="requestedTripForReview && changesMade">
        <TripReviewMetadata
          :vehicles="requestedTripForReview.vehicles"
          :passengerCount="requestedTripForReview.passengerCount"
          :tripTypeId="requestedTripForReview.tripTypeId"
          :tripTypeChanged="requestedTripForReview.tripTypeChanged"
          :passengerCountChanged="requestedTripForReview.passengerCountChanged"
          :vehiclesChanged="requestedTripForReview.vehiclesChanged"
        />
        <v-card class="background-white margin-t-4">
          <v-card-text class="padding-t-4 padding-b-0 padding-x-4">
            <template
              v-if="
                requestedTripForReview.ada ||
                requestedTripForReview.spab ||
                requestedTripForReview.vehicleNeededEntireTrip
              "
            >
              <TripReviewRequirements
                :ada="requestedTripForReview.ada"
                :spab="requestedTripForReview.spab"
                :vehicleNeededEntireTrip="
                  requestedTripForReview.vehicleNeededEntireTrip
                "
                :adaChanged="requestedTripForReview.adaChanged"
                :spabChanged="requestedTripForReview.spabChanged"
                :vehicleNeededEntireTripChanged="
                  requestedTripForReview.vehicleNeededEntireTripChanged
                "
              />
              <v-divider />
            </template>
            <TripReviewItinerary
              :stops="requestedTripForReview.stops"
              class="margin-t-4"
            />
          </v-card-text>
        </v-card>
      </template>
      <template v-else>
        <p>
          No changes were specified.
        </p>
      </template>
    </div>
    <TripModificationPayment v-if="paymentUpdate" :show-titles="isDraftCustomerReview" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, inject } from 'vue'
import TripReviewItinerary from '@/components/TripReviewItinerary.vue'
import TripReviewRequirements from '@/components/TripReviewRequirements.vue'
import TripReviewMetadata from '@/components/TripReviewMetadata.vue'
import tripModificationStore from '@/store/modules/tripModification'
import TripModificationPayment from './TripModificationPayment.vue'

const removePadding = inject('removePadding', false)

const currentTrip = computed(() => tripModificationStore.currentTrip)
const requestedTripForReview = computed(() => tripModificationStore.requestedTripForReview)
const paymentUpdate = computed(() => tripModificationStore.paymentUpdate)

const changesMade = computed(() => {
  const req = requestedTripForReview.value
  if (!req) return false
  const requirementsModified =
    req.adaChanged ||
    req.spabChanged ||
    req.vehicleNeededEntireTripChanged
  const metadataModified =
    req.passengerCountChanged ||
    req.tripTypeChanged ||
    req.vehiclesChanged
  const stopsModified = req.stops?.some((stop: any) => stop.stopChanged)
  return requirementsModified || metadataModified || stopsModified
})

const isMobile = computed(() => {
  return (window as any).$vuetify?.breakpoint?.xsOnly
})

const isDraftCustomerReview = computed(() => tripModificationStore.isDraftCustomerReview)

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })

  tripModificationStore.setForwardButtonDisabled(false)
})
</script>
